<template>
  <div class="composite-panel-host">
    <!-- 子屏：常驻缓存，切换仅 display:none（renderer 内部 visible=true 时才会首次拉数） -->
    <PanelScreenRenderer
      v-for="t in panelTabs"
      v-show="isTabActive(t) && activeContentType === 'panel'"
      :key="`${groupId || 'g'}:panel:${String(t.tabId)}`"
      :panel-sn="tabPanelSnString(t)"
      :visible="isTabActive(t) && activeContentType === 'panel'"
      :width="hostWidthNum"
      :height="hostHeightNum"
      :tab-scope-id="tabScopeId"
      :composite-nest-level="compositeNestLevel" />

    <!-- 第三方网页：shell 上设置 urlInset 内边距，iframe 填满内容区 -->
    <div
      v-for="t in urlTabs"
      :key="`${groupId || 'g'}:url:${String(t.tabId)}`"
      :class="['composite-panel-host__url-shell', { 'is-active': isTabActive(t) && activeContentType === 'url' && !isMixedContentBlockedUrl(tabUrlString(t)) }]"
      :style="urlTabShellInsetStyle(t)">
      <iframe
        class="composite-panel-host__iframe-inner"
        :src="tabUrlString(t)"
        frameborder="0"
        allowfullscreen
        referrerpolicy="no-referrer"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads" />
    </div>

    <!-- 混合内容提示：保留在 DOM 中，避免看起来“空白” -->
    <div
      v-show="activeContentType === 'url' && !!activeUrl && isMixedContentBlockedUrl(activeUrl)"
      class="composite-panel-host__empty">
      当前大屏为 https，浏览器会拦截 iframe 加载 http 链接（混合内容）。
      <div style="margin-top:10px;">
        <a class="composite-panel-host__link" :href="activeUrl" target="_blank" rel="noopener noreferrer">新窗口打开</a>
        <a
          v-if="activeUrlHttpsUpgrade"
          class="composite-panel-host__link"
          :href="activeUrlHttpsUpgrade"
          target="_blank"
          rel="noopener noreferrer"
          style="margin-left:12px;">尝试 https</a>
      </div>
    </div>

    <div v-if="panels.length && activeTabIdStr && activeContentType === 'panel' && !activePanelTabId" class="composite-panel-host__empty">
      当前页签未绑定子屏
    </div>
    <div v-else-if="!panels.length" class="composite-panel-host__empty">
      请先在「菜单Tab」中配置子屏
    </div>
  </div>
</template>

<script>
import PanelScreenRenderer from '@/components/charts/panelScreenRenderer.vue'
import { getActiveTabId, setActiveTabId } from '@/utils/compositeTabState'
import { normalizeCompositeTabUrlInset } from '@/utils/compositeScreen'

export default {
  name: 'CompositePanelHost',
  components: { PanelScreenRenderer },
  props: {
    config: { type: Object, required: true },
    globalConfig: { type: Object, default: null },
    componentType: { type: String, default: '' },
    componentStatus: { type: String, default: 'view' },
    /** 嵌套渲染时隔离 tab 激活态，避免不同组合屏串状态 */
    tabScopeId: { type: [String, Number], default: 'global' },
    /** 子屏容器链路上的组合嵌套深度（根组合内第一层子屏为 0） */
    compositeNestLevel: { type: Number, default: 0 },
  },
  computed: {
    groupId () {
      return this.config?.chartSet?.groupId || ''
    },
    partnerTabs () {
      const views = this.globalConfig?.chartViews || []
      const gid = this.groupId
      let bar = views.find(
        (v) => v.chartComponent === 'compositeTabBar' &&
          v.chartSet &&
          v.chartSet.groupId === gid,
      )
      if (!bar) {
        const bars = views.filter((v) => v.chartComponent === 'compositeTabBar' && v.chartSet)
        if (bars.length === 1) bar = bars[0]
      }
      if (!bar || !bar.chartSet) return []
      return Array.isArray(bar.chartSet.tabs) ? bar.chartSet.tabs : []
    },
    panels () {
      if (this.componentStatus === 'edit') {
        return this.partnerTabs.filter(t => t && t.tabId)
      }
      return this.partnerTabs.filter(t => t && t.tabId && (this.tabType(t) === 'url' ? this.tabUrlString(t) : this.tabPanelSnString(t)))
    },
    panelTabs () {
      return (this.panels || []).filter((t) => t && t.tabId != null && this.tabType(t) === 'panel' && this.tabPanelSnString(t))
    },
    /** 与 tab 上 tabId 统一成字符串再比，避免接口数字与前端字符串不一致导致全不显示 */
    activeTabIdStr () {
      const id = getActiveTabId(this.groupId, this.tabScopeId)
      return id == null || id === '' ? '' : String(id)
    },
    hostWidthNum () {
      const n = Number(this.config.chartWidth)
      return Number.isFinite(n) && n > 0 ? n : 800
    },
    hostHeightNum () {
      const n = Number(this.config.chartHeight)
      return Number.isFinite(n) && n > 0 ? n : 600
    },
    /** 已绑定子屏的页签（用于：无可用子屏时把激活态落到第一个有子屏的 tab） */
    panelsWithSn () {
      return this.panels
        .map((t) => {
          if (!t || !t.tabId) return null
          const sn = this.tabPanelSnString(t)
          if (!sn) return null
          return { tabId: t.tabId, panelSn: sn }
        })
        .filter(Boolean)
    },
    /** 当前选中页签对应的子屏 id（单子屏数据源） */
    activePanelSn () {
      const id = this.activeTabIdStr
      const list = this.panels || []
      // 若 activeTab 未初始化，回落到第一个已绑定子屏的 tab，避免嵌套组合屏出现“外层有、内部空白”
      if (!id) {
        const fb = list.find((x) => x && x.tabId != null && (this.tabType(x) === 'url' ? this.tabUrlString(x) : this.tabPanelSnString(x)))
        return fb ? this.tabPanelSnString(fb) : ''
      }
      const t = list.find((x) => x && String(x.tabId) === id)
      if (!t) {
        const fb = list.find((x) => x && x.tabId != null && (this.tabType(x) === 'url' ? this.tabUrlString(x) : this.tabPanelSnString(x)))
        return fb ? this.tabPanelSnString(fb) : ''
      }
      return this.tabPanelSnString(t)
    },
    activePanelTabId () {
      if (this.activeContentType !== 'panel') return ''
      const id = this.activeTabIdStr
      const list = this.panelTabs || []
      if (id && list.some((x) => String(x.tabId) === String(id))) return id
      const fb = list[0]
      return fb && fb.tabId != null ? String(fb.tabId) : ''
    },
    activeContentType () {
      const id = this.activeTabIdStr
      const list = this.panels || []
      const t = id ? list.find((x) => x && String(x.tabId) === id) : null
      const effective = t || list.find((x) => x && x.tabId != null && (this.tabType(x) === 'url' ? this.tabUrlString(x) : this.tabPanelSnString(x))) || null
      return effective ? this.tabType(effective) : 'panel'
    },
    activeUrl () {
      const id = this.activeTabIdStr
      const list = this.panels || []
      const t = id ? list.find((x) => x && String(x.tabId) === id) : null
      const effective = t || list.find((x) => x && x.tabId != null && this.tabType(x) === 'url' && this.tabUrlString(x)) || null
      return effective ? this.tabUrlString(effective) : ''
    },
    /** url 类型的 tabs（用于 iframe 常驻缓存） */
    urlTabs () {
      return (this.panels || []).filter((t) => t && t.tabId != null && this.tabType(t) === 'url' && this.tabUrlString(t))
    },
    activeUrlTabId () {
      if (this.activeContentType !== 'url') return ''
      const id = this.activeTabIdStr
      const list = this.urlTabs || []
      if (id && list.some((x) => String(x.tabId) === String(id))) return id
      const fb = list[0]
      return fb && fb.tabId != null ? String(fb.tabId) : ''
    },
    activeUrlHttpsUpgrade () {
      if (!this.activeUrl) return ''
      const u = String(this.activeUrl).trim()
      if (u.toLowerCase().startsWith('http://')) return `https://${u.slice('http://'.length)}`
      return ''
    },
    /** 切换 tab 或子屏 id 时强制重挂载，保证 load 与内部 ref 一致 */
    activePanelRenderKey () {
      const kind = this.activeContentType
      const ident = kind === 'url' ? (this.activeUrl || 'url') : (this.activePanelSn || 'sn')
      return `${this.groupId || 'g'}:${this.activeTabIdStr || 't'}:${kind}:${ident}`
    },
  },
  watch: {
    /** 激活项须对应仍存在 tabId；仅当当前 id 已从页签列表消失时才改到「首个已绑子屏」页签 */
    panels: {
      immediate: true,
      deep: true,
      handler () {
        if (!this.groupId) return
        const tabRows = this.panels.filter(Boolean)
        const allIds = new Set(tabRows.map((p) => String(p.tabId)))
        const cur = getActiveTabId(this.groupId, this.tabScopeId)
        const curStr = cur == null || cur === '' ? '' : String(cur)
        if (curStr && allIds.has(curStr)) return
        const withSn = this.panelsWithSn
        if (withSn.length) {
          setActiveTabId(this.groupId, String(withSn[0].tabId), this.tabScopeId)
          return
        }
        const fb = tabRows[0]
        if (fb && fb.tabId != null) setActiveTabId(this.groupId, String(fb.tabId), this.tabScopeId)
      },
    },
  },
  methods: {
    isTabActive (t) {
      if (!t || t.tabId == null) return false
      return String(t.tabId) === String(this.activeTabIdStr || '')
    },
    urlTabShellInsetStyle (t) {
      const inset = normalizeCompositeTabUrlInset(t)
      return {
        paddingTop: `${inset.top}px`,
        paddingRight: `${inset.right}px`,
        paddingBottom: `${inset.bottom}px`,
        paddingLeft: `${inset.left}px`,
      }
    },
    isMixedContentBlockedUrl (url) {
      // https 页面内嵌 http iframe 会被浏览器直接拦截（mixed content）
      if (!url) return false
      if (typeof window === 'undefined' || !window.location) return false
      try {
        const pageProto = String(window.location.protocol || '').toLowerCase()
        const u = String(url).trim().toLowerCase()
        return pageProto === 'https:' && u.startsWith('http://')
      } catch (e) {
        return false
      }
    },
    tabType (t) {
      const v = t && t.type != null ? String(t.type).trim().toLowerCase() : ''
      return v === 'url' ? 'url' : 'panel'
    },
    /** el-select 等可能把 value 存成数字；须为合法非空字符串供接口与 prop 使用 */
    tabPanelSnString (t) {
      if (!t) return ''
      const raw = t.panelSn ?? t.panel_sn
      if (raw == null || raw === '') return ''
      const p = raw
      if (typeof p === 'string' || typeof p === 'number') return String(p)
      if (typeof p === 'object') {
        if (p.panelSn != null && p.panelSn !== '') return String(p.panelSn)
        if (p.value != null && p.value !== '') return String(p.value)
      }
      return ''
    },
    tabUrlString (t) {
      if (!t) return ''
      const raw = t.url
      if (raw == null || raw === '') return ''
      return String(raw).trim()
    },
  },
}
</script>

<style lang="scss" scoped>
.composite-panel-host {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
.composite-panel-host__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
}
.composite-panel-host__url-shell {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  overflow: hidden;
  // 重要：不要用 display:none 隐藏，否则第三方页面内的 ECharts 等可能在 0 尺寸时初始化
  visibility: hidden;
  pointer-events: none;
  z-index: 0;
}
.composite-panel-host__url-shell.is-active {
  visibility: visible;
  pointer-events: auto;
  z-index: 1;
}
.composite-panel-host__iframe-inner {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: rgba(0, 0, 0, 0);
}
.composite-panel-host__link {
  color: rgba(64, 158, 255, 0.95);
  text-decoration: underline;
}
</style>
